import Autocomplete from '@mui/material/Autocomplete';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import MDBox from 'components/MDBox';
import { IconButton, styled } from '@mui/material';
import { useSuggestTermsQuery } from 'services/analyticsApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { History } from '@mui/icons-material';
import { useCookies } from 'react-cookie';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';
import MDTypography from 'components/MDTypography';
import { updateGlobalSearchQuery, updateGlobalSearchResultCleared } from 'slice/searchSlice';
import MDInput from 'components/MDInput';

export default function GlobalSearchCombobox() {
  const { formatMessage: msg } = useIntl();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchState = useSelector((state) => state.search);

  const [query, setQuery] = useState('');

  const queryHistoryCookieName = 'search_query_history';
  const [cookies, setCookie] = useCookies([queryHistoryCookieName]);
  const queryHistoryList = cookies.search_query_history ?? [];
  const termQuery = useSuggestTermsQuery({ query, maxResult: 12 });

  useEffect(() => {
    if (searchState.globalSearchQuery) {
      setQuery(searchState.globalSearchQuery);
    }
  }, [searchState]);

  const handleInputChange = useCallback(
    (event, newInputValue) => {
      dispatch(updateGlobalSearchResultCleared(true));
      if (newInputValue?.length > 2) {
        setQuery(newInputValue);
      }
      if (!newInputValue) {
        setQuery('');
      }
      dispatch(updateGlobalSearchQuery(newInputValue));
    },
    [dispatch]
  );

  const debouncedHandleInputChange = useMemo(
    () => debounce(handleInputChange, 300),
    [handleInputChange]
  );

  function addToQueryHistory(query) {
    if (!queryHistoryList.some((item) => item.label === query) && query?.length > 2) {
      queryHistoryList.unshift({ label: query, history: true });
      queryHistoryList.length = Math.min(queryHistoryList.length, 5);
      setCookie(queryHistoryCookieName, queryHistoryList, { path: '/' });
    }
  }

  const GroupItems = styled('ul')({
    padding: 0
  });

  const goToGlobalSearchResult = (query) => {
    addToQueryHistory(query.label);
    navigate('/portal/globalSearch');
  };

  const handleAutocompleteValueChange = (event, newValue) => {
    if (newValue?.label?.length > 2) {
      setQuery(newValue.label);
      goToGlobalSearchResult(newValue);
    }
  };

  const handleSearchButtonClicked = () => {
    goToGlobalSearchResult(query);
  };

  const buildOptions = () =>
    query?.length > 2
      ? termQuery?.data?.map((item) => ({
          ...item,
          group: msg({ id: 'page.search.suggestion.group' })
        })) ?? []
      : [
          ...queryHistoryList.map((item) => ({
            ...item,
            group: msg({ id: 'page.search.lastResult.group' })
          })),
          ...(termQuery?.data?.map((item) => ({
            ...item,
            group: msg({ id: 'page.search.suggestion.group' })
          })) ?? [])
        ];

  return (
    <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={msg({ id: 'action.search' })}>
        <IconButton
          aria-label={msg({ id: 'common.search' })}
          color="text"
          onClick={handleSearchButtonClicked}
        >
          <SearchOutlinedIcon sx={{ mr: 1, my: 0.5 }} fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Autocomplete
        id="global-search-combo-box"
        value={query}
        freeSolo
        onBlur={() => addToQueryHistory(query)}
        options={buildOptions()}
        onChange={handleAutocompleteValueChange}
        onInputChange={debouncedHandleInputChange}
        filterOptions={(x) => x}
        groupBy={(option) => option.group}
        renderOption={(props, option) => (
          <MDBox
            {...props}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              '&:hover': {
                transition: 'background 1s ease-out',
                background: ({ functions: { linearGradient }, palette: { gradients } }) =>
                  `${linearGradient(gradients.hover.main, gradients.hover.state)}`
              }
            }}
          >
            <div>{option.label}</div>
            {option.history && <History sx={{ marginLeft: 'auto' }} />}
          </MDBox>
        )}
        renderGroup={(params) => (
          <li key={params.group}>
            <MDBox
              sx={{ position: 'sticky', top: '-8px', padding: '4px 10px' }}
              bgColor="container"
            >
              <MDTypography variant="button" fontWeight="bold">
                {params.group}
              </MDTypography>
            </MDBox>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        sx={{ width: 300 }}
        renderInput={(params) => (
          <MDInput {...params} label={msg({ id: 'action.search' })} variant="filled" />
        )}
      />
    </MDBox>
  );
}
